import axios from 'axios'

import { buildFactorialIdUrl } from './buildUrl'

// Shared token refresh state across all HTTP clients (Axios, Apollo, etc.)
let isRefreshingToken = false
let refreshPromise: Promise<boolean> | null = null

/**
 * Attempts to refresh the Factorial ID access token using the refresh token
 * This function is shared across Axios interceptors and Apollo error links
 * to prevent duplicate refresh requests.
 *
 * @returns Promise<boolean> - true if refresh was successful, false otherwise
 */
export const refreshFactorialIdToken = async (): Promise<boolean> => {
  // If a refresh is already in progress, wait for it
  if (isRefreshingToken && refreshPromise) {
    return refreshPromise
  }

  // Start a new refresh
  isRefreshingToken = true

  refreshPromise = (async () => {
    try {
      // Use a fresh axios instance without interceptors to avoid infinite loops
      // If we use the same axios instance, the refresh request will be intercepted
      // by this interceptor and it will try to refresh the token again, causing an infinite loop.
      const refreshAxios = axios.create({
        baseURL: buildFactorialIdUrl('/api'),
        timeout: 10000,
        withCredentials: true,
      })

      await refreshAxios.post('/auth/refresh')
      return true
    } catch (error) {
      return false
    } finally {
      isRefreshingToken = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Checks if the user is authenticated with Factorial ID
 *
 * The _factorial_id_data cookie is ONLY set by Factorial ID, never by the legacy auth system.
 * Therefore, the presence of this cookie is a reliable indicator that the user is using
 * Factorial ID authentication (which means the company has the feature flag enabled).
 *
 * @returns boolean - true if user has the _factorial_id_data cookie
 */
export const isFactorialIdUser = (): boolean => {
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('_factorial_id_data='))
}
